import React, { useEffect, useRef, useState } from 'react';

// ROUND FLOATING MENU (owner 2026-10-13: "create a round button which has all menus like Dashboard etc.,
// remove it from the top; when the page moves it moves with the page"). Fixed on screen while scrolling;
// tap = open/close the menu, drag = move the button (position remembered per device).
const SIZE = 56;
const KEY = 'fab_pos';

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function loadPos() {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (p && typeof p.x === 'number' && typeof p.y === 'number') return p;
  } catch (e) { /* storage blocked — use default */ }
  return null;
}

export default function FloatingMenu({ items, current, onSelect }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(() => loadPos());
  const drag = useRef(null);
  const wrap = useRef(null);

  // default spot: bottom-right, above the phone's bottom edge
  const place = () => {
    const w = window.innerWidth, h = window.innerHeight;
    const p = pos || { x: w - SIZE - 18, y: h - SIZE - 24 };
    return { x: clamp(p.x, 8, w - SIZE - 8), y: clamp(p.y, 8, h - SIZE - 8) };
  };
  const [, force] = useState(0);
  useEffect(() => {
    const onResize = () => force(n => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const cur = place();

  const onPointerDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: cur.x, oy: cur.y, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;          // small wiggle = still a tap
    d.moved = true;
    setPos({ x: clamp(d.ox + dx, 8, window.innerWidth - SIZE - 8), y: clamp(d.oy + dy, 8, window.innerHeight - SIZE - 8) });
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.moved) {
      try { localStorage.setItem(KEY, JSON.stringify(place())); } catch (e) { /* ignore */ }
    } else {
      setOpen(o => !o);
    }
  };

  // open the menu toward the side with more room
  const openUp = cur.y > window.innerHeight / 2;
  const openLeft = cur.x > window.innerWidth / 2;

  return (
    <div ref={wrap} className="fab-wrap" style={{ left: cur.x, top: cur.y }}>
      {open && (
        <div className="fab-menu" role="menu"
             style={{ [openUp ? 'bottom' : 'top']: SIZE + 10, [openLeft ? 'right' : 'left']: 0 }}>
          {items.map(it => (
            <button key={it.id} role="menuitem"
                    className={`fab-item ${current === it.id ? 'active' : ''}`}
                    onClick={() => { onSelect(it.id); setOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
              <span className="fab-ico">{it.icon}</span>{it.label}
            </button>
          ))}
        </div>
      )}
      <button className={`fab-btn ${open ? 'open' : ''}`} aria-label="Menu" aria-expanded={open}
              onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
              onPointerCancel={() => { drag.current = null; }}>
        {open ? '✕' : '☰'}
      </button>
    </div>
  );
}
